import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { zoneService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";
import ModelItem from "/app/src/components/generic/components/explorer/modelItems";

/**
 * SingleZone renders the zone details along with its locations in a table format.
 *
 * @returns {JSX.Element} The rendered component displaying zone information and locations.
 */
export default function SingleZone() {
  const { t } = useTranslation();
  const handleNavigationError = useHandleNavigationError();
  const { zoneId } = useParams();

  // Get the Single Zone from URL
  const { data: zone } = useQuery({
    queryKey: ["zone", zoneId],
    queryFn: () => {
      return zoneService.getSingle(zoneId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { zone: {} },
    retry: false,
    select: (data: { zone: ZoneType }) => {
      return data.zone;
    },
  });

  const basicInfo = [
    { label: t("translation:name"), value: zone?.name },
    {
      label: t("translation:type"),
      value:
        zone?.type === 1 ? t("translation:picking") : t("translation:putting"),
    },
    // { label: t("translation:description"), value: zone?.description },
  ];

  const zoneProperties = [
    { label: t("translation:pick_type"), value: zone?.pickTypeDescription },
    { label: t("translation:put_type"), value: zone?.putTypeDescription },
    { label: t("translation:abc_class"), value: zone?.abcClass },
    { label: t("translation:colour"), value: zone?.color },
  ];

  const zoneLocations = zone?.locations?.map((location) => ({
    id: location.id,
    name: location.name,
  }));

  const zoneShelves = zone?.shelves?.map((shelf) => ({
    id: shelf.id,
    name: shelf.name,
  }));

  const zoneMaterialProperties = zone?.materialProperties?.map(
    (materialProperty) => ({
      id: materialProperty.id,
      name: materialProperty.name,
    }),
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{zone?.name}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:zones"), to: "/explorer/zones" },
          { label: zone?.name },
        ]}
      />
      <h1 className="text-[36px] font-medium">{zone?.name}</h1>
      <h2 className="text-[16px] mb-6 text-neutral-smoke">
        Zone Id: {zone?.id}
      </h2>
      <InfoList header={t("translation:basic_info")} items={basicInfo} />
      <InfoList
        header={t("translation:zone_properties")}
        items={zoneProperties}
      />
      <ModelItem modelName="Locations" items={zoneLocations} />
      <ModelItem clickable={false} modelName="Shelves" items={zoneShelves} />
      <ModelItem
        modelName="Material Properties"
        items={zoneMaterialProperties}
      />
    </div>
  );
}
